import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import { useAuthStore } from "@/stores/authStore"
import { api } from "@/lib/api"
import { StatCard } from "@/components/ui/stat-card"
import { StatusBadge, CallStatusBadge } from "@/components/ui/status-badge"
import { CardGridSkeleton, ListSkeleton } from "@/components/ui/data-table-skeleton"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { PatientAvatar } from "@/components/ui/avatar"
import {
  Users,
  Phone,
  CheckSquare,
  TrendingUp,
  ArrowRight,
  Clock,
  AlertCircle,
} from "lucide-react"
import { format, formatDistanceToNow } from "date-fns"
import { parseApiTimestamp } from "@/lib/datetime"

interface DashboardData {
  apcmStats: {
    patients: number
    apcm_consented_patients: number
    approved_plans_of_care: number
    checkins_this_month: number
  } | null
  recentCalls: any[]
  actionItems: any[]
  campaigns: any[]
}

export default function DashboardPage() {
  const { selectedOrganizationId } = useOrganizationsStore()
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<DashboardData>({
    apcmStats: null,
    recentCalls: [],
    actionItems: [],
    campaigns: [],
  })

  useEffect(() => {
    if (!selectedOrganizationId) return

    const fetchDashboardData = async () => {
      setLoading(true)
      try {
        // Fetch data in parallel for speed
        const [apcmStatsRes, callsRes, actionItemsRes, campaignsRes] = await Promise.all([
          api.getAPCMStats(selectedOrganizationId).catch(() => null),
          api.queryCallsV3(selectedOrganizationId, { limit: 10, sort_by: "call_started_at", sort_order: "desc" }).catch(() => ({ calls: [] })),
          api.getOrganizationActionItems(selectedOrganizationId).catch(() => ({ items: [] })),
          api.getCampaigns(selectedOrganizationId).catch(() => []),
        ])

        setData({
          apcmStats: apcmStatsRes,
          recentCalls: callsRes?.calls || [],
          actionItems: actionItemsRes?.items || [],
          campaigns: campaignsRes || [],
        })
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [selectedOrganizationId])

  const greeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return "Good morning"
    if (hour < 18) return "Good afternoon"
    return "Good evening"
  }

  const urgentActionItems = data.actionItems.filter(
    (item: any) => item.priority === "high" || item.priority === "urgent"
  )

  const todaysCalls = data.recentCalls.filter((call: any) => {
    const callDate = parseApiTimestamp(call.call_started_at || call.created_at)
    const today = new Date()
    return callDate?.toDateString() === today.toDateString()
  })

  return (
    <div className="mx-auto max-w-7xl space-y-6 md:space-y-8 p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">
            {greeting()}{user?.email ? `, ${user.email.split("@")[0]}` : ""}
          </h1>
          <p className="text-muted-foreground">
            {format(new Date(), "EEEE, MMMM d, yyyy")}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <CardGridSkeleton cards={4} />
      ) : (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Patients in APCM"
            value={data.apcmStats?.apcm_consented_patients || 0}
            subtitle={`of ${data.apcmStats?.patients || 0} total patients`}
            icon={<Users className="h-5 w-5" />}
          />
          <StatCard
            title="Calls Today"
            value={`${todaysCalls.filter((c: any) => c.call_status === "completed").length}/${todaysCalls.length}`}
            subtitle={todaysCalls.length > 0 ? `${Math.round((todaysCalls.filter((c: any) => c.call_status === "completed").length / todaysCalls.length) * 100)}% completed` : "No calls yet"}
            icon={<Phone className="h-5 w-5" />}
          />
          <StatCard
            title="Active Campaigns"
            value={data.campaigns.filter((c: any) => c.status === "active").length}
            subtitle={`${data.campaigns.length} total`}
            icon={<TrendingUp className="h-5 w-5" />}
          />
          <StatCard
            title="Action Items"
            value={data.actionItems.filter((i: any) => i.status !== "completed").length}
            subtitle={urgentActionItems.length > 0 ? `${urgentActionItems.length} urgent` : "All caught up"}
            icon={<CheckSquare className="h-5 w-5" />}
          />
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Priority Actions */}
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              Priority Actions
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/action-items">
                View All <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <ListSkeleton items={3} />
            ) : urgentActionItems.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <CheckSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No urgent actions</p>
              </div>
            ) : (
              <div className="space-y-3">
                {urgentActionItems.slice(0, 5).map((item: any) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.title}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {item.patient_name || "No patient assigned"}
                      </p>
                    </div>
                    <StatusBadge variant="destructive" size="sm">
                      Urgent
                    </StatusBadge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Campaigns */}
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
            <CardTitle className="text-base font-semibold">Active Campaigns</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/campaigns">
                Manage <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <ListSkeleton items={3} />
            ) : data.campaigns.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <Phone className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No active campaigns</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.campaigns
                  .filter((c: any) => c.status === "active")
                  .slice(0, 4)
                  .map((campaign: any) => (
                    <div
                      key={campaign.id}
                      className="flex items-center gap-3 rounded-lg border p-3"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{campaign.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{campaign.enrolled_count || 0} enrolled</span>
                          {campaign.success_rate && (
                            <>
                              <span>•</span>
                              <span>{Math.round(campaign.success_rate * 100)}% success</span>
                            </>
                          )}
                        </div>
                      </div>
                      <StatusBadge variant="success" dot>
                        Active
                      </StatusBadge>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Call Activity */}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
          <CardTitle className="text-base font-semibold">Recent Call Activity</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/calls">
              View All Calls <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <ListSkeleton items={5} />
          ) : data.recentCalls.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <Phone className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No recent calls</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.recentCalls
                .filter((call: any) => call.call_status !== "pending")
                .slice(0, 8)
                .map((call: any) => {
                const patientName = call.patient_first_name && call.patient_last_name
                  ? `${call.patient_first_name} ${call.patient_last_name}`
                  : call.patient_name || "Unknown Patient";
                return (
                <Link
                  key={call.id}
                  to={`/calls/${call.id}`}
                  className="flex items-center gap-4 rounded-lg border p-3 transition-colors hover:bg-muted/50"
                >
                  <PatientAvatar
                    name={patientName}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {patientName}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {call.strategy_name || call.strategy_type || "Call"}
                    </p>
                  </div>
                  <div className="text-right">
                    <CallStatusBadge status={call.call_status || "unknown"} />
                    <p className="text-xs text-muted-foreground mt-1">
                      {call.call_started_at
                        ? formatDistanceToNow(parseApiTimestamp(call.call_started_at) ?? new Date(), { addSuffix: true })
                        : "Pending"}
                    </p>
                  </div>
                  {call.duration_seconds && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {Math.floor(call.duration_seconds / 60)}:{String(call.duration_seconds % 60).padStart(2, "0")}
                    </div>
                  )}
                </Link>
              );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
